import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, FileDown } from "lucide-react";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [sheets, setSheets] = useState([]);
    const { clear } = useCart();
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    if (!cleared.current) {
                        cleared.current = true;
                        clear();
                    }
                    axios
                        .get(`${API}/orders/${sessionId}/care-sheets`)
                        .then((r) => setSheets(r.data))
                        .catch(() => {});
                    return;
                }
            } catch {
                /* keep polling */
            }
            attempts += 1;
            if (attempts < 15) setTimeout(poll, 2000);
            else setStatus("pending");
        };
        poll();
    }, [sessionId, clear]);

    return (
        <main className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="payment-success-page">
            <Seo title="Order Confirmed — Logan's Reptiles & Exotics" siteName="Logan's Reptiles & Exotics" />
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="max-w-lg border border-red-900/50 bg-coal p-10 text-center"
            >
                {status === "paid" ? (
                    <>
                        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                        <h1 className="mt-6 font-display text-3xl font-extrabold text-bone">Order secured.</h1>
                        <p className="mt-4 text-sm leading-relaxed text-ash" data-testid="payment-success-message">
                            Payment confirmed. We'll email your receipt and reach out to arrange
                            climate-controlled overnight freight. Welcome to the wild side.
                        </p>
                        {sheets.length > 0 && (
                            <div className="mt-6 border border-red-950/50 bg-black/40 p-5 text-left" data-testid="care-sheet-downloads">
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-red-500">
                                    Your care sheets
                                </p>
                                {sheets.map((s) => (
                                    <a
                                        key={s.product_id}
                                        data-testid="care-sheet-download-link"
                                        href={`${process.env.REACT_APP_BACKEND_URL}${s.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-3 flex items-center gap-2 border border-red-900/50 px-4 py-3 font-mono2 text-[11px] uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-crimson"
                                    >
                                        <FileDown className="h-3.5 w-3.5 text-red-500" /> Care Sheet — {s.name}
                                    </a>
                                ))}
                                <p className="mt-3 font-mono2 text-[9px] uppercase tracking-[0.15em] text-ash/50">
                                    Links are also in your confirmation email
                                </p>
                            </div>
                        )}
                    </>
                ) : status === "checking" ? (
                    <>
                        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-red-900 border-t-red-500" />
                        <h1 className="mt-6 font-display text-2xl font-bold text-bone">Confirming payment…</h1>
                        <p className="mt-3 text-sm text-ash">Checking with Stripe — one moment.</p>
                    </>
                ) : (
                    <>
                        <CheckCircle2 className="mx-auto h-12 w-12 text-amber-500" />
                        <h1 className="mt-6 font-display text-2xl font-bold text-bone">Almost there.</h1>
                        <p className="mt-3 text-sm text-ash" data-testid="payment-pending-message">
                            Your payment is still processing. If you were charged, your order is safe —
                            contact us and we'll confirm it manually.
                        </p>
                    </>
                )}
                <Link
                    to="/shop"
                    data-testid="payment-success-continue"
                    className="mt-8 inline-block border border-red-900/60 px-8 py-3 font-mono2 text-xs uppercase tracking-[0.25em] text-ash transition-colors duration-300 hover:bg-crimson hover:text-bone"
                >
                    Back to the Collection
                </Link>
            </motion.div>
        </main>
    );
};

export const PaymentCancel = () => (
    <main className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="payment-cancel-page">
        <Seo title="Checkout Cancelled — Logan's Reptiles & Exotics" siteName="Logan's Reptiles & Exotics" />
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-lg border border-red-900/50 bg-coal p-10 text-center"
        >
            <XCircle className="mx-auto h-12 w-12 text-red-600" />
            <h1 className="mt-6 font-display text-3xl font-extrabold text-bone">Checkout cancelled.</h1>
            <p className="mt-4 text-sm text-ash">
                No charge was made. Your enclosure is still waiting whenever you're ready.
            </p>
            <Link
                to="/shop"
                data-testid="payment-cancel-return"
                className="mt-8 inline-block bg-crimson px-8 py-3 font-mono2 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-red-700"
            >
                Return to the Shop
            </Link>
        </motion.div>
    </main>
);
